// Central registry of the primary list views.
//
// Keyed by route name and mapped by hand on purpose - none of this can be
// derived from the URL path:
//   /activities   renders MyActivities.vue, whose Vuex module is registered
//                 under the lowercase key 'myactivities' (see store/store.js)
//   /myActivities renders Activities.vue, whose module is 'activities'
//   /newContacts  renders NewContacts.vue
// Deriving the label or the module from the path mislabels all three.
//
//   label       what the breadcrumb shows. Kept in step with the nav menu in
//               App.vue so a crumb reads the same as the item the user clicked.
//   storeModule the Vuex module holding that list's persisted view state.

export const LIST_ROUTES = {
  'customers': { label: 'Customers', storeModule: 'customers' },
  'enquiries': { label: 'Quotations', storeModule: 'enquiries' },
  'quotations': { label: 'Booked Quotations', storeModule: 'quotations' },
  'tasks': { label: 'Tasks', storeModule: 'tasks' },
  'activities': { label: 'Events', storeModule: 'myactivities' },
  // P7 part 2: tasks and events together, over db_view 14 (no kind in the list request).
  'allActivities': { label: 'All Activities', storeModule: 'allActivities' },
  'myactivities': { label: 'Events (old)', storeModule: 'activities' },
  'contacts': { label: 'Contacts (old)', storeModule: 'contacts' },
  // Shares the 'contacts' module with the old list above, which is how it has
  // always worked - the registered 'newContacts' module is dead code.
  // Deliberately left shared: AppTopbar.loadFilter picks the module by
  // lowercasing the server-supplied favouriteName prefix, so a filter saved as
  // "Contacts:..." only reaches this list while it reads the contacts module.
  // Splitting them needs that prefix confirmed against live data first.
  'new contacts': { label: 'Contacts', storeModule: 'contacts' },
  'users': { label: 'Users', storeModule: 'users' }
}

// How a list crumb says what it is showing (item 2 UAT, Mohammed 2026-09-28):
//
//   a saved filter is selected, search or not -> "Customers (Filter: Peter Lord DKS)"
//   only a search or quick filter             -> "Customers (Filtered)"
//   nothing                                   -> "Customers"
//
// A name longer than SAVED_FILTER_NAME_MAX is cut with an ellipsis, and `title` (the crumb's
// tooltip) then carries the whole crumb. `title` is left off when nothing was cut.
export const SAVED_FILTER_NAME_MAX = 40

export function listCrumbLabel(listLabel, { savedFilterName: name = '', filtered = false } = {}) {
  if (name) {
    const cut = name.length > SAVED_FILTER_NAME_MAX
    const shown = cut ? `${name.slice(0, SAVED_FILTER_NAME_MAX).trimEnd()}\u2026` : name
    const crumb = { label: `${listLabel} (Filter: ${shown})` }
    if (cut) crumb.title = `${listLabel} (Filter: ${name})`
    return crumb
  }
  return { label: filtered ? `${listLabel} (Filtered)` : listLabel }
}

// The name of the saved filter a list module holds, or '' when none is selected. The list's own
// dropdown stores the filter row (`filterName`); a topbar favourite stores the same row, but only
// "<view>: <name>" in `favouriteName` is guaranteed there.
export function savedFilterName(saved) {
  if (!saved || !saved.id) return ''
  if (saved.filterName) return String(saved.filterName).trim()
  const fav = saved.favouriteName ? String(saved.favouriteName) : ''
  const colon = fav.indexOf(':')
  return colon >= 0 ? fav.slice(colon + 1).trim() : ''
}

// The Vuex modules App.setupFirstLoad marks for a first load, one per list. A list
// loads the user's default saved filter only while its module is marked, so this is
// read from LIST_ROUTES rather than written out by hand - the hand list named
// 'activities' and missed 'myactivities' and 'users', and neither list ever opened
// on its default filter.
export function firstLoadModules() {
  return [...new Set(Object.values(LIST_ROUTES).map(list => list.storeModule))]
}

// Detail route names all start with one of these prefixes. Maps a detail route
// back to the list route it belongs to, so the Back interceptor and the
// breadcrumb agree on where "up" is.
//
// The fallback is only used when there is no recorded page to return to (a deep
// link straight into a detail URL, or a fresh tab), otherwise the interceptor
// returns to the full path + query the user actually came from.
// `param` is the route param holding the record id. The breadcrumb trail uses
// it to tell one record from another, so that moving between tabs of the same
// record updates the existing crumb instead of adding a new one.
export const DETAIL_PREFIXES = {
  'customersChild': { list: 'customers', label: 'Customer', param: 'custid' },
  'enquiriesChild': { list: 'enquiries', label: 'Quotation', param: 'custid' },
  'quotationsChild': { list: 'quotations', label: 'Booked Quotation', param: 'quoteVerId' },
  // Tasks and meetings share /activities/:id. `listForType` names the list a record belongs to
  // when its page publishes a record type (BreadcrumbRecordMixin): a task's page says "Task",
  // so the menu lights Tasks (see menuPathFor). The crumb and everything else keep `list`.
  'activitiesChild': { list: 'activities', label: 'Event', param: 'activityId', listForType: { Task: 'tasks' } },
  'contactsChild': { list: 'new contacts', label: 'Contact', param: 'contactId' },
  'usersChild': { list: 'users', label: 'User', param: 'userid' },
  'rolesChild': { list: 'roles', label: 'Role', param: 'roleid' }
}

// Label for the tab crumb inside a detail page, keyed by route name.
//
// Keyed by name rather than by the trailing path segment because the same
// segment is labelled differently per module: /detail is "Info" on a customer
// but "Details" on a user, and /history is "History" on a customer but "Login
// History" on a user. These match the TabMenu labels in the *TabsDetails
// components, so a crumb reads the same as the tab it points at.
export const TAB_LABELS = {
  customersChildDetail: 'Info',
  customersChildContacts: 'Contacts',
  customersChildLinkedaccounts: 'Linked Accounts',
  customersChildNotes: 'Notes',
  customersChildQuotations: 'Quotations',
  customersChildActivities: 'Tasks & Events',
  customersChildAttachments: 'Attachments',
  customersChildSales: 'Sales Stats',
  customersChildHistory: 'History',

  enquiriesChildDetail: 'Info',
  enquiriesChildQuotations: 'Quotations',
  enquiriesChildActivities: 'Tasks & Events',
  enquiriesChildNotes: 'Notes',
  enquiriesChildAttachments: 'Attachments',
  enquiriesChildHistory: 'History',

  quotationsChildDetail: 'Info',
  quotationsChildVersions: 'Quotations',
  quotationsChildActivities: 'Tasks & Events',
  quotationsChildAttachments: 'Attachments',
  quotationsChildHistory: 'History',

  activitiesChildDetail: 'Info',
  activitiesChildHistory: 'History',

  contactsChildDetail: 'Info',

  usersChildDetail: 'Details',
  usersChildHistory: 'Login History',
  usersChildDashboard: 'Dashboard',
  usersChildWidgets: 'Widgets',

  rolesChildDetail: 'Details',
  rolesChildPermissions: 'Permissions'
}

// Where a saved filter from the topbar favourites belongs, keyed by the
// lowercased db_view.view_name.
//
// The backend names a favourite "<dbview.viewName>: <filterName>"
// (UserViewFilterControllerHandler). AppTopbar used to lowercase that prefix
// and use it directly as both a Vuex module key and a route name, which only
// works for the view names that happen to lowercase into one. 'My Activities',
// 'Actual Sales' and 'Sales Forecast' lowercase to strings matching no module
// and no route, so those favourites dispatched into nothing and failed
// silently inside an empty catch.
//
// Note the activities pair is crossed, and not the way the names suggest:
// db_view 5 "Activities" is rendered by Activities.vue at /myActivities
// (route name 'myactivities', module 'activities'), while db_view 7
// "My Activities" is rendered by MyActivities.vue at /activities (route name
// 'activities', module 'myactivities').
//
//   module: null means the list has no saved-filter state to load into.
//   ActualSales.vue and SalesForcast.vue only mention MainListSavedFilter in
//   commented-out code and their Vuex modules have no setMainListSavedFilter,
//   so there is nothing to dispatch to - the user gets told instead.
//
//   'activities' and 'contacts' name the two retired lists (see RETIRED_LIST_ROUTES),
//   so a favourite still carrying either prefix goes to the current list.
export const FAVOURITE_VIEWS = {
  'customers': { module: 'customers', route: 'customers' },
  'contacts': { module: 'contacts', route: 'new contacts' },
  'enquiries': { module: 'enquiries', route: 'enquiries' },
  'quotations': { module: 'quotations', route: 'quotations' },
  'tasks': { module: 'tasks', route: 'tasks' },
  'activities': { module: 'myactivities', route: 'activities' },
  'my activities': { module: 'myactivities', route: 'activities' },
  // P7: db_view 5 and 7 are renamed "Events" and "My Events" (db/phase3/fixes/P7 in the backend).
  'events': { module: 'myactivities', route: 'activities' },
  'my events': { module: 'myactivities', route: 'activities' },
  'all activities': { module: 'allActivities', route: 'allActivities' },
  'users': { module: 'users', route: 'users' },
  'actual sales': { module: null, route: 'actualSales' },
  'sales forecast': { module: null, route: 'salesForcast' }
}

// The same lists keyed by db_view id, which a favourite carries as `dbview.id`.
// The id is authoritative: two views are both named "Contacts" (2 is the old
// list at /contacts, 9 the current one at /newContacts), so the name alone sent
// a view-9 favourite to the old list, whose dropdown could not show it and whose
// endpoint answered 500 on its clause. Since the old lists were retired, 2 and 5 go
// to the current lists as well.
export const FAVOURITE_VIEWS_BY_ID = {
  6: FAVOURITE_VIEWS['customers'],
  1: FAVOURITE_VIEWS['customers'],
  2: { module: 'contacts', route: 'new contacts' },
  9: { module: 'contacts', route: 'new contacts' },
  8: FAVOURITE_VIEWS['enquiries'],
  3: FAVOURITE_VIEWS['enquiries'],
  4: FAVOURITE_VIEWS['quotations'],
  5: FAVOURITE_VIEWS['my activities'],
  7: FAVOURITE_VIEWS['my activities'],
  13: FAVOURITE_VIEWS['tasks'],
  14: FAVOURITE_VIEWS['all activities'],
  12: FAVOURITE_VIEWS['users'],
  10: FAVOURITE_VIEWS['actual sales'],
  11: FAVOURITE_VIEWS['sales forecast']
}

// Lists retired on 2026-09-13 (Mohammed: "yes retire them"), each mapped to the
// current list that replaces it. Both were already out of the nav menu in App.vue,
// but bookmarks, favourites and ActivityFormPage still opened them, and their saved
// filters were moved to the current lists (backend db/phase3/10). The route names
// stay in the router as redirects, so anything that still names them lands on the
// current list; the components are left in place, unrouted, so this is reversible.
export const RETIRED_LIST_ROUTES = {
  'myactivities': 'activities',
  'contacts': 'new contacts'
}

// vue-router redirect for a retired list route: the current list, same query, so a
// `?search=` carried by an old link still applies.
export function redirectForRetiredList(retiredName, to) {
  return { name: RETIRED_LIST_ROUTES[retiredName], query: to.query }
}

// Resolves a favourite to its list, or null if unknown. Accepts the favourite
// object (preferred: routes by dbview.id, falling back to the favouriteName
// prefix) or a bare view name.
export function favouriteTargetFor(favourite) {
  if (!favourite) return null
  if (typeof favourite === 'string') {
    return FAVOURITE_VIEWS[favourite.trim().toLowerCase()] || null
  }
  const id = favourite.dbview && favourite.dbview.id
  if (id != null && FAVOURITE_VIEWS_BY_ID[id]) return FAVOURITE_VIEWS_BY_ID[id]
  const name = favourite.favouriteName ? String(favourite.favouriteName).split(':')[0] : ''
  return FAVOURITE_VIEWS[name.trim().toLowerCase()] || null
}

// Returns the DETAIL_PREFIXES entry for a route name, or null.
export function detailEntryFor(routeName) {
  if (!routeName) return null
  const prefix = Object.keys(DETAIL_PREFIXES).find(p => routeName.startsWith(p))
  return prefix ? DETAIL_PREFIXES[prefix] : null
}

// How many crumbs the trail shows at once. Beyond this the oldest drop off the
// front, so a long drill-down stays readable.
export const TRAIL_LENGTH = 3

// Identifies the breadcrumb trail node a route belongs to.
//
//   list   -> one node per list, so arriving at a list restarts the trail
//   record -> one node per record, NOT per tab: /customers/12/notes and
//             /customers/12/contacts are the same node, so moving between tabs
//             updates the crumb rather than stacking another one
//
// Returns null for anything outside the registry (settings, reports), which
// leaves the trail untouched rather than polluting it.
export function trailNodeFor(route) {
  if (!route || !route.name) return null

  const list = LIST_ROUTES[route.name]
  if (list) {
    return { kind: 'list', key: `list:${route.name}`, listRoute: route.name, label: list.label }
  }

  const detail = detailEntryFor(route.name)
  if (detail) {
    const id = route.params && route.params[detail.param]
    return {
      kind: 'record',
      key: `record:${detail.list}:${id}`,
      typeLabel: detail.label,
      label: detail.label
    }
  }

  return null
}

// The left-menu path of the module a route belongs to (2026-09-27): a record page belongs to its
// list's module (/customers/12/notes -> /customers, /contacts/5/detail -> /newContacts), anything
// else to its own path. AppSubmenu highlights the menu item with this path, so the highlight
// follows the route on every navigation, Back and Forward included. The menu's router-links are
// exact matches and, on their own, light nothing on a record page.
// `recordType` is the kind the record page on screen published ("Task", or '' for none): where the
// page's entry has a list for that type, the record belongs to that list's module instead
// (2026-09-28: a task at /activities/71/detail lights Tasks, a meeting there Activities).
export function menuPathFor(route, router, recordType) {
  if (!route) return null
  const detail = detailEntryFor(route.name)
  if (detail && router) {
    const listName = (recordType && detail.listForType && detail.listForType[recordType]) || detail.list
    try {
      const list = router.resolve({ name: listName }).route
      if (list && list.matched && list.matched.length) return list.path
    } catch (e) {
      // An unknown list route: fall back to the page's own path.
    }
  }
  return route.path
}
